import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Theme,
} from '@material-ui/core';
import { Edit, Delete } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableContainer: {
      marginTop: '1rem',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: '8px',
      overflow: 'hidden',
    },
    tableHeader: {
      // No background color - matches Activity Applications/Waitlistings pattern
    },
    tableHeaderCell: {
      fontWeight: 600,
      fontSize: '12px',
      color: theme.color.grey.neutralBrand500,
      borderBottom: `1px solid ${theme.color.grey.border}`,
      padding: '16px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.color.grey.neutralBrand50,
      },
      borderBottom: `1px solid ${theme.color.grey.border}`,
    },
    tableCell: {
      borderBottom: 'none',
      padding: '16px',
      fontSize: '14px',
      verticalAlign: 'middle',
    },
    actionButtons: {
      display: 'flex',
      gap: '4px',
      justifyContent: 'flex-end',
    },
    actionButton: {
      padding: '4px',
      '&:hover': {
        backgroundColor: theme.color.grey.neutralBrand100,
      },
    },
    editButton: {
      color: theme.color.grey.neutralBrand500,
    },
    deleteButton: {
      color: theme.color.grey.neutralBrand500,
    },
    reminderTypeChip: {
      fontSize: '11px',
      height: '22px',
      minWidth: '80px',
    },
    publishedRosterChip: {
      backgroundColor: theme.alert.blue.light,
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    taskExpiryBeforeChip: {
      backgroundColor: '#7B1FA2',
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    taskExpiryAfterChip: {
      backgroundColor: '#00695C',
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    birthdayReminderChip: {
      backgroundColor: '#E91E63',
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    anniversaryReminderChip: {
      backgroundColor: '#FF9800',
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    smsRosterAttendanceChip: {
      backgroundColor: '#4CAF50',
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    smsRosterReminderChip: {
      backgroundColor: '#2196F3',
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    applicationReminderAfterChip: {
      backgroundColor: '#F57C00',
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    statusChip: {
      fontSize: '11px',
      height: '22px',
      minWidth: '60px',
    },
    enabledChip: {
      backgroundColor: theme.status.green.light,
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    disabledChip: {
      backgroundColor: theme.color.grey.neutralBrand100,
      color: theme.color.grey.neutralBrand500,
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: theme.color.grey.neutralBrand500,
    },
  })
);

export interface ReminderConfig {
  tenantReminderConfigId: string;
  timeBefore: number;
  reminderType:
    | 'published-roster'
    | 'task-expiry-before'
    | 'task-expiry-after'
    | 'birthday-reminder'
    | 'anniversary-reminder'
    | 'sms-roster-attendance'
    | 'sms-roster-reminder'
    | 'application-reminder-after';
  customContent?: string | null;
  isEnabled: boolean;
}

interface ReminderConfigTableProps {
  reminderConfigs: ReminderConfig[];
  onEdit: (config: ReminderConfig) => void;
  onDelete: (configId: string) => void;
  loading?: boolean;
  error?: any;
}

const ReminderConfigTable: React.FC<ReminderConfigTableProps> = ({
  reminderConfigs,
  onEdit,
  onDelete,
  loading = false,
  error,
}) => {
  const classes = useStyles();

  const getReminderTypeDisplay = (type: string) => {
    switch (type) {
      case 'published-roster':
        return 'Published Roster Reminder';
      case 'task-expiry-before':
        return 'Task Expiry Reminder Before Expiry';
      case 'task-expiry-after':
        return 'Task Expiry Reminder After Expiry';
      case 'birthday-reminder':
        return 'Birthday Message';
      case 'anniversary-reminder':
        return 'Anniversary Message';
      case 'sms-roster-attendance':
        return 'SMS Roster Attendance';
      case 'sms-roster-reminder':
        return 'SMS Roster Reminder';
      case 'application-reminder-after':
        return 'Application Reminder After';
      default:
        return type;
    }
  };

  const getDaysBeforeDisplay = (days: number, reminderType: string) => {
    const isSmsType = reminderType === 'sms-roster-attendance' || reminderType === 'sms-roster-reminder';
    const isAfterType = reminderType === 'task-expiry-after' || reminderType === 'application-reminder-after';

    if (days === 0) {
      return 'On the day';
    }

    if (isSmsType) {
      if (days === 1) {
        return '1 hour before';
      }
      return `${days} hours before`;
    }

    if (isAfterType) {
      if (days === 1) {
        return '1 day after';
      }
      return `${days} days after`;
    }

    if (days === 1) {
      return '1 day before';
    }
    return `${days} days before`;
  };

  if (loading) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant='body2'>Loading reminders...</Typography>
      </Box>
    );
  }

  if (error) {
    const { lightTextColor } = getAlertCardColors('urgent');
    return (
      <Box style={{ marginTop: '1rem' }}>
        <AlertCard variant='urgent' title='Error!'>
          <AlertCardBody>
            <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
              Volaby has experienced an error. {error.message || 'Unknown error occurred'}
            </Typography>
          </AlertCardBody>
        </AlertCard>
      </Box>
    );
  }

  if (!reminderConfigs || reminderConfigs.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant='body2'>
          No reminders found. Click &quot;Add Reminder&quot; to create your first automated reminder.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table>
        <TableHead className={classes.tableHeader}>
          <TableRow>
            <TableCell className={classes.tableHeaderCell}>Type</TableCell>
            <TableCell className={classes.tableHeaderCell}>Timing</TableCell>
            {/* <TableCell className={classes.tableHeaderCell}>Custom Message</TableCell> */}
            <TableCell className={classes.tableHeaderCell}>Status</TableCell>
            <TableCell className={classes.tableHeaderCell} align='center'>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reminderConfigs.map((config) => (
            <TableRow key={config.tenantReminderConfigId} className={classes.tableRow}>
              <TableCell className={classes.tableCell}>
                <Chip
                  label={getReminderTypeDisplay(config.reminderType)}
                  className={`${classes.reminderTypeChip} ${
                    config.reminderType === 'published-roster'
                      ? classes.publishedRosterChip
                      : config.reminderType === 'task-expiry-before'
                      ? classes.taskExpiryBeforeChip
                      : config.reminderType === 'task-expiry-after'
                      ? classes.taskExpiryAfterChip
                      : config.reminderType === 'birthday-reminder'
                      ? classes.birthdayReminderChip
                      : config.reminderType === 'anniversary-reminder'
                      ? classes.anniversaryReminderChip
                      : config.reminderType === 'sms-roster-attendance'
                      ? classes.smsRosterAttendanceChip
                      : config.reminderType === 'sms-roster-reminder'
                      ? classes.smsRosterReminderChip
                      : config.reminderType === 'application-reminder-after'
                      ? classes.applicationReminderAfterChip
                      : classes.anniversaryReminderChip
                  }`}
                  size='small'
                />
              </TableCell>
              <TableCell className={classes.tableCell}>
                {getDaysBeforeDisplay(config.timeBefore, config.reminderType)}
              </TableCell>
              {/* <TableCell className={classes.tableCell}>
                {config.customContent ? (
                  <Typography variant='body2' style={{ maxWidth: '300px' }}>
                    {config.customContent.length > 100
                      ? `${config.customContent.substring(0, 100)}...`
                      : config.customContent}
                  </Typography>
                ) : (
                  <Typography variant='body2' style={{ fontStyle: 'italic', color: theme.color.grey.neutralBrand400 }}>
                    Default message
                  </Typography>
                )}
              </TableCell> */}
              <TableCell className={classes.tableCell}>
                <Chip
                  label={config.isEnabled ? 'Enabled' : 'Disabled'}
                  className={`${classes.statusChip} ${config.isEnabled ? classes.enabledChip : classes.disabledChip}`}
                  size='small'
                />
              </TableCell>
              <TableCell className={classes.tableCell} align='right'>
                <Box className={classes.actionButtons}>
                  <IconButton
                    className={`${classes.actionButton} ${classes.editButton}`}
                    onClick={() => onEdit(config)}
                    size='small'
                    title='Edit reminder'
                  >
                    <Edit fontSize='small' />
                  </IconButton>
                  <IconButton
                    className={`${classes.actionButton} ${classes.deleteButton}`}
                    onClick={() => onDelete(config.tenantReminderConfigId)}
                    size='small'
                    title='Delete reminder'
                  >
                    <Delete fontSize='small' />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { ReminderConfigTable };
